import { NodeHtmlMarkdown, TranslatorConfigObject } from 'node-html-markdown';

import { generateHTML, JSONContent, mergeAttributes } from '@tiptap/core';

import {
  sharedMainEditorExtensions,
  convertContactCardToMarkdown,
} from '@distribute/shared/tiptap/extensions';

import {
  XmlCustomTaskList,
  XmlTimelineItem,
  XmlTimeline,
  XmlContactCard,
  XmlSnippetBlock,
  XmlVideoRecord,
  XmlCallout,
  XmlFilePreview,
  XmlTodoList,
  XmlTodoListItem,
  XmlToggleList,
  XmlToggleListItem,
  XmlToggleListItemContent,
  XmlIframe,
} from '@distribute/shared/tiptap/xml-extensions';

import { CodeBlockExt } from '@distribute/shared/tiptap/extensions';

// Unicode character to start a block
const START_BLOCK = '\uE000START_BLOCK\uE000';

// Override the CodeBlock renderHTML to correctly parse it using NodeHtmlMarkdown
const _CodeBlock = CodeBlockExt.extend({
  renderHTML({ node, HTMLAttributes }) {
    return [
      'pre',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: 'code-block__text',
      }),
      [
        'code',
        {
          class: node.attrs.language
            ? this.options.languageClassPrefix + node.attrs.language
            : null,
        },
        0,
      ],
    ];
  },
});

export const generateMarkdown = (content: JSONContent) => {
  const html = generateHTML(content, [
    ...sharedMainEditorExtensions,
    _CodeBlock,
    XmlTimeline,
    XmlTimelineItem,
    XmlCustomTaskList,
    XmlSnippetBlock,
    XmlVideoRecord,
    XmlContactCard,
    XmlCallout,
    XmlFilePreview,
    XmlTodoList,
    XmlTodoListItem,
    XmlToggleList,
    XmlToggleListItem,
    XmlToggleListItemContent,
    XmlIframe,
  ]);

  const translators: TranslatorConfigObject = {
    'Timeline,TimelineItem,CustomTaskList,SnippetBlock,VideoRecord': {
      ignore: true,
    },
    ContactCard: ({ node }) => {
      const name = node.getAttribute('name') ?? '';
      const initialName = node.getAttribute('initialName') ?? '';
      const jobTitle = node.getAttribute('jobTitle') ?? '';
      const company = node.getAttribute('company') ?? '';
      const description = node.getAttribute('description') ?? '';
      const serializedContacts = node.getAttribute('serializedContacts') ?? '';

      return {
        surroundingNewlines: 2,
        content: convertContactCardToMarkdown({
          name,
          initialName,
          jobTitle,
          company,
          description,
          serializedContacts,
        }),
      };
    },
    Callout: ({ node }) => {
      const emoji = node.getAttribute('emoji') ?? '';

      return {
        surroundingNewlines: 2,
        postprocess: ({ content }) => {
          // Remove leading newlines and whitespace
          return emoji + ' ' + content.replace(/^[\n\s]+/g, '');
        },
      };
    },
    FilePreview: ({ node }) => {
      const url = node.getAttribute('fileUrl') ?? '';
      const name = node.getAttribute('fileName') ?? 'File';

      return {
        surroundingNewlines: 2,
        content: `[${name}](${url})`,
      };
    },
    video: ({ node }) => {
      const src = node.getAttribute('src') ?? '';

      return {
        surroundingNewlines: 2,
        content: `[Video](${src})`,
      };
    },
    TodoList: () => {
      return { surroundingNewlines: 1 };
    },
    TodoListItem: ({ node }) => {
      const checked = node.getAttribute('data-checked') === 'true';

      return {
        postprocess: ({ content }) => {
          // Remove leading newlines and whitespace and trailing newline
          return (
            `- [${checked ? 'x' : ' '}] ` +
            content.replace(/^[\n\s]+/g, '').replace(/\n{1}$/g, '')
          );
        },
      };
    },
    ToggleList: () => {
      return { surroundingNewlines: 1 };
    },
    ToggleListItem: () => {
      return {
        surroundingNewlines: 1,
        postprocess: ({ content }) => {
          return content.split(START_BLOCK)[1];
        },
      };
    },
    ToggleListItemContent: ({ node }) => {
      const text = node.previousElementSibling?.textContent ?? 'Toggle';

      return {
        surroundingNewlines: 1,
        postprocess: ({ content }) => {
          return `${START_BLOCK}<details><summary>${text}</summary>\n\n${content}</details>`;
        },
      };
    },
    Iframe: ({ node }) => {
      const src = node.getAttribute('src') ?? '';

      return {
        content: `[URL](${src})`,
      };
    },
  };

  const nhm = new NodeHtmlMarkdown({}, translators);

  return nhm.translate(html);
};
